import { Link, NavLink, useNavigate } from "react-router-dom";
import { Leaf, NotebookPen, MessageCircleHeart, LogOut, Users, Inbox, Library, BookOpen, Smile, Briefcase, Radio, MessagesSquare, Heart, CalendarCheck, CreditCard } from "lucide-react";
import { homeFor } from "@/components/RequireRole";
import { EDITIONS } from "@/lib/brand";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useProfile } from "@/context/ProfileContext";
import { GradeSelect } from "@/components/GradeSelect";
import { BuddyAvatar } from "@/components/buddy/BuddyAvatar";
import { NotificationBell } from "@/components/Notifications";
import { InstallApp } from "@/components/InstallApp";
import { MessagesLink } from "@/components/messages/MessagesLink";
import { canSwitchEdition, ParentEditionDialog } from "@/components/EditionSwitch";
import { useState } from "react";
import { SpanishSwitch } from "@/components/SpanishTap";

const navCls = ({ isActive }) =>
  `px-4 py-2 rounded-full text-sm font-medium transition-colors ${isActive ? "bg-[#2C4A3E] text-white" : "text-stone-600 hover:bg-[#F1EADB]"}`;

const UserMenu = ({ profile, logout }) => {
  const nav = useNavigate();
  const { setStateEdition } = useProfile();
  const [pending, setPending] = useState(null);
  const pick = (k) => (k === profile.state ? null : profile.role === "parent" ? setPending(k) : setStateEdition(k));
  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="user-menu-trigger" className="shrink-0 flex items-center gap-2 pl-1 focus:outline-none">
        <span className="w-9 h-9 rounded-full bg-[#E8B4B8] grid place-items-center font-semibold text-[#2C4A3E]">{profile.name[0]?.toUpperCase()}</span>
        <span className="hidden text-sm leading-tight text-left"><b className="block text-stone-800">{profile.name}</b><span className="text-stone-500 text-xs capitalize">{profile.role}</span></span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#FDFBF7]">
        <DropdownMenuLabel className="text-xs text-stone-500 font-normal">{profile.email}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {canSwitchEdition(profile) ? <>
          {Object.entries(EDITIONS).map(([k, v]) => (
            <DropdownMenuItem key={k} data-testid={`edition-switch-${k}`} onClick={() => pick(k)}>
              {profile.state === k ? "● " : "○ "}{v}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
        </> : <DropdownMenuLabel className="text-xs text-stone-500 font-normal" data-testid="edition-current">{EDITIONS[profile.state] || EDITIONS.ga}</DropdownMenuLabel>}
        {profile.role === "student" && <div className="xl:hidden">
          {[["/planner", "Plan", CalendarCheck], ["/journal", "Journal", NotebookPen], ...(profile.learner_type !== "adult" ? [["/live", "Live classes", Radio], ["/chat", "School-Day Chat", MessagesSquare]] : []), ["/buddy", "Buddy", Smile]].map(([to, label, Icon]) => (
            <DropdownMenuItem key={to} data-testid={`menu-nav-${to.slice(1)}`} onClick={() => nav(to)}><Icon className="w-4 h-4 mr-2" /> {label}</DropdownMenuItem>))}
          <DropdownMenuSeparator />
        </div>}
        {(profile.role !== "student" || profile.learner_type === "adult") && <DropdownMenuItem data-testid="menu-billing" onClick={() => nav("/billing")}>
          <CreditCard className="w-4 h-4 mr-2" /> Billing & plan
        </DropdownMenuItem>}
        <DropdownMenuItem data-testid="menu-curriculum" onClick={() => nav("/curriculum")}>
          <BookOpen className="w-4 h-4 mr-2" /> Curriculum
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="logout-btn" onClick={async () => { await logout(); nav("/login"); }}>
          <LogOut className="w-4 h-4 mr-2" /> Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    <ParentEditionDialog state={pending} onClose={() => setPending(null)} />
    </>
  );
};

export const pathwayEligible = (p) => p?.learner_type === "adult" || ["9", "10", "11", "12"].includes(p?.grade);

const StudentNav = ({ setTutorOpen, buddy, profile }) => (
  <>
    <nav className="hidden xl:flex items-center gap-1">
      <NavLink to="/" end className={navCls} data-testid="nav-home">Learn</NavLink>
      <NavLink to="/planner" className={navCls} data-testid="nav-planner" title="Plan" aria-label="Plan">
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><CalendarCheck className="w-4 h-4" /><span className="sr-only">Plan</span></span>
      </NavLink>
      <NavLink to="/journal" className={navCls} data-testid="nav-journal" title="Journal" aria-label="Journal">
        <span className="inline-flex items-center gap-1.5"><NotebookPen className="w-4 h-4" /><span className="sr-only">Journal</span></span>
      </NavLink>
      {pathwayEligible(profile) && <NavLink to="/pathway" className={navCls} data-testid="nav-pathway" title="Pathway" aria-label="Pathway">
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><Briefcase className="w-4 h-4" /><span className="sr-only">Pathway</span></span>
      </NavLink>}
      {profile?.learner_type !== "adult" && <NavLink to="/live" className={navCls} data-testid="nav-live" title="Live" aria-label="Live">
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><Radio className="w-4 h-4" /><span className="sr-only">Live</span></span>
      </NavLink>}
      {profile?.learner_type !== "adult" && <NavLink to="/chat" className={navCls} data-testid="nav-school-chat" title="Chat" aria-label="Chat">
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><MessagesSquare className="w-4 h-4" /><span className="sr-only">Chat</span></span>
      </NavLink>}
      <NavLink to="/buddy" className={navCls} data-testid="nav-buddy" title="Buddy" aria-label="Buddy">
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><Smile className="w-4 h-4 shrink-0" /><span className="sr-only">{buddy?.name || "Buddy"}</span></span>
      </NavLink>
    </nav>
    <NavLink to="/buddy" className="xl:hidden p-2 rounded-full text-[#2C4A3E] hover:bg-[#F1EADB]" data-testid="nav-buddy-mobile" aria-label="Buddy">
      <Smile className="w-5 h-5" />
    </NavLink>
    <NavLink to="/planner" className="xl:hidden p-2 rounded-full text-[#2C4A3E] hover:bg-[#F1EADB]" data-testid="nav-planner-mobile" aria-label="Plan" title="Plan">
      <CalendarCheck className="w-5 h-5" />
    </NavLink>
    <NavLink to="/journal" className="xl:hidden p-2 rounded-full text-[#2C4A3E] hover:bg-[#F1EADB]" data-testid="nav-journal-mobile" aria-label="Journal">
      <NotebookPen className="w-5 h-5" />
    </NavLink>
    {profile.learner_type !== "adult" && <GradeSelect testId="header-grade-select" className="w-[96px] sm:w-[130px] shrink-0" />}
    <button onClick={() => setTutorOpen(true)} data-testid="header-open-tutor-btn" title={buddy ? `Talk to ${buddy.name}` : "Talk"}
      className="hidden sm:inline-flex items-center gap-2 whitespace-nowrap px-3 py-2 rounded-full border border-[#E6DEC3] text-sm text-[#2C4A3E] hover:bg-[#F1EADB] transition-colors">
      {buddy ? <BuddyAvatar buddy={buddy} className="w-6 h-6" testId="header-buddy-avatar" /> : <MessageCircleHeart className="w-4 h-4" />} <span className="sr-only">{buddy ? `Talk to ${buddy.name}` : "Talk"}</span>
    </button>
  </>
);

const GuestNav = () => (
  <div className="flex items-center gap-2">
    <Link to="/pricing" data-testid="nav-guest-pricing" className="hidden sm:inline-flex px-4 py-2 rounded-full text-sm text-[#2C4A3E] hover:bg-[#F1EADB] transition-colors">Pricing</Link>
    <Link to="/curriculum" data-testid="nav-guest-curriculum" className="hidden sm:inline-flex px-4 py-2 rounded-full text-sm text-[#2C4A3E] hover:bg-[#F1EADB] transition-colors">Curriculum</Link>
    <Link to="/login" data-testid="nav-login-btn" className="px-4 py-2 rounded-full text-sm text-[#2C4A3E] hover:bg-[#F1EADB] transition-colors">Log in</Link>
    <Link to="/register" data-testid="nav-register-btn" className="px-4 py-2 rounded-full text-sm bg-[#2C4A3E] text-white hover:bg-[#1E352B] transition-colors">Sign up</Link>
  </div>
);

export const Header = () => {
  const { profile, setTutorOpen, logout, buddy, guestState } = useProfile();
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#FDFBF7]/80 border-b border-[#E6DEC3]/70">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-3 flex items-center gap-2 sm:gap-4 min-h-[60px]">
        <Link to={homeFor(profile?.role, profile?.learner_type)} data-testid="nav-logo" className="flex items-center gap-2.5 mr-auto shrink-0">
          <span className="w-9 h-9 shrink-0 rounded-full bg-[#2C4A3E] grid place-items-center"><Leaf className="w-4 h-4 text-[#F7F3EB]" /></span>
          <span className="hidden sm:inline font-display text-xl font-semibold text-[#2C4A3E] whitespace-nowrap">ConsciousLearn</span>
          <span className="hidden xl:inline text-[10px] uppercase tracking-[0.2em] whitespace-nowrap text-[#C87D53] font-semibold border border-[#E6DEC3] rounded-full px-2 py-0.5" data-testid="edition-badge">{EDITIONS[profile?.state || guestState] || EDITIONS.ga}</span>
        </Link>
        <div className="flex items-center gap-1 sm:gap-2 min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {profile === false && <GuestNav />}
        {profile?.role === "student" && <StudentNav setTutorOpen={setTutorOpen} buddy={buddy} profile={profile} />}
        {profile?.role === "teacher" && (
          <NavLink to="/teacher" className={navCls} data-testid="nav-teacher-classes">
            <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4" />Classes</span>
          </NavLink>
        )}
        {profile?.role === "parent" && (
          <NavLink to="/parent" className={navCls} data-testid="nav-parent-kids">
            <span className="inline-flex items-center gap-1.5"><Heart className="w-4 h-4" />My Kids</span>
          </NavLink>
        )}
        {profile?.role === "parent" && (
          <Link to="/parent?view=baby" className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm text-[#2C4A3E] hover:bg-[#F1EADB] transition-colors" data-testid="nav-parent-baby">Conscious Baby</Link>
        )}
        {profile?.role === "daycare" && (
          <NavLink to="/daycare" className={navCls} data-testid="nav-daycare">
            <span className="inline-flex items-center gap-1.5"><Heart className="w-4 h-4" />My Center</span>
          </NavLink>
        )}
        {profile?.role === "admin" && (
          <>
            <NavLink to="/admin" end className={navCls} data-testid="nav-admin-leads">
              <span className="inline-flex items-center gap-1.5"><Inbox className="w-4 h-4" />Leads</span>
            </NavLink>
            <NavLink to="/admin/courses" className={navCls} data-testid="nav-admin-courses">
              <span className="inline-flex items-center gap-1.5"><BookOpen className="w-4 h-4" />Courses</span>
            </NavLink>
            <NavLink to="/admin/library" className={navCls} data-testid="nav-admin-library">
              <span className="inline-flex items-center gap-1.5"><Library className="w-4 h-4" />Library</span>
            </NavLink>
            <NavLink to="/admin/live" className={navCls} data-testid="nav-admin-live">
              <span className="inline-flex items-center gap-1.5"><Radio className="w-4 h-4" />Live</span>
            </NavLink>
          </>
        )}
        {profile && !["admin", "student"].includes(profile.role) && (
          <NavLink to="/curriculum" className={`${navCls({ isActive: false })} hidden lg:inline-flex`} data-testid="nav-curriculum">Curriculum</NavLink>
        )}
        {profile !== null && <span className="hidden sm:contents"><InstallApp /></span>}
        </div>
        {profile?.role === "student" && <SpanishSwitch />}
        {["student", "parent", "teacher"].includes(profile?.role) && <div className="flex items-center shrink-0"><MessagesLink /><NotificationBell /></div>}
        {profile && <UserMenu profile={profile} logout={logout} />}
      </div>
    </header>
  );
};
